import styles from "./your-first-visit.module.css";
import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { OptimizedImage } from "~/components/OptimizedImage";

type Localization = {
  title: string;
  startYourNewSmileHeading: string;
  startYourNewSmileP1: string;
  startYourNewSmileP2: string;
  firstConsultationHeading: string;
  firstConsultationP1: string;
  firstConsultationP2: string;
  firstConsultationP3: string;
  firstConsultationP4: string;
  firstConsultationP5: string;
  firstConsultationP6: string;
  firstConsultationP7: string;
  firstConsultationP8: string;
  startingTreatmentHeading: string;
  startingTreatmentP1: string;
  startingTreatmentP2: string;
  followUpHeading: string;
  followUpP1: string;
  followUpP2: string;
  getStartedHeading: string;
  getStartedP1: string;
  getStartedP2: string;
};

const englishLocalization: Localization = {
  title: "Your First Visit",
  startYourNewSmileHeading: "Start Your New Smile",
  startYourNewSmileP1:
    "Here's how to begin your smile journey at Miami Orthodontic Specialists in Miami, FL.",
  startYourNewSmileP2:
    "The first visit to a doctor's office can sometimes seem intimidating, especially for kids. But, your first visit to Miami Orthodontic Specialists will be completely painless.",
  firstConsultationHeading: "Your First Free Consultation Appointment",
  firstConsultationP1:
    "We believe that your first orthodontic visit is your most important one. Your first appointment will begin with a tour of our office and an introduction to the members of our team.",
  firstConsultationP2:
    "You'll be welcomed by our team, introduced to our orthodontist Dr. Garcia-Paul and shown around our office. Our highly-trained, orthodontic treatment coordinator will then take some digital photographs of your teeth. If necessary, a panoramic and cephalometric X-ray will be taken. These images are the best way for us to see your teeth, jaws, and facial soft tissue.",
  firstConsultationP3:
    "After reviewing these records, Dr. Garcia-Paul will join you in a private consultation room to discuss your individualized and unique treatment plan. Dr. Garcia-Paul will point out specific areas of concern and show you what kind of corrections you can expect by showing photos and videos of similar cases.",
  firstConsultationP4:
    "During your comprehensive initial exam, Dr. Garcia-Paul will develop a personalized treatment plan that is tailored to your specific needs. We will help you understand how orthodontic treatment can have a positive impact for you or your child.",
  firstConsultationP5:
    "She will take as much time as needed to answer your questions and decide what kind of treatment is best for you. Dr. Garcia-Paul and her assistant will then walk you through the process.",
  firstConsultationP6:
    "After your consultation, you will have ample opportunity to meet with our financial coordinator to discuss treatment costs, insurance options, and payment plans.",
  firstConsultationP7:
    "If you are considering treatment, our team will be happy to answer any questions that you have and to help you determine which treatment is right for you.",
  firstConsultationP8:
    "Call our office now to schedule your first free consultation.",
  startingTreatmentHeading: "Starting Treatment",
  startingTreatmentP1:
    "Our office offers a convenient, same-day start program. If you are ready to begin treatment, we can place the braces or take your Invisalign scan on the same day as your consult!",
  startingTreatmentP2:
    "This allows you to avoid taking additional time off from school or work for the bonding appointment.",
  followUpHeading: "Follow-Up Appointments",
  followUpP1:
    "During treatment, appointments will be scheduled every 6 to 8 weeks. The appointments will vary in length from 20 to 40 minutes, depending on the adjustments that are required.",
  followUpP2:
    "We make every effort to run on time for our patients and ask that you extend the same courtesy to us. If you cannot make it to a scheduled appointment, please contact our office as soon as possible.",
  getStartedHeading: "Get Started",
  getStartedP1:
    "If you have any more questions about your first orthodontic visit, please call us at 305-407-9000.",
  getStartedP2:
    "You can also schedule a free consultation by filling out a simple online form.",
};

const spanishLocalization: Localization = {
  title: "Su primera visita",
  startYourNewSmileHeading: "Comienza tu nueva sonrisa",
  startYourNewSmileP1:
    "Aquí le mostramos cómo comenzar su viaje hacia la sonrisa en Miami Orthodontic Specialists en Miami, FL.",
  startYourNewSmileP2:
    "La primera visita al consultorio del médico puede resultar intimidante, especialmente para los niños. Sin embargo, su primera visita a Miami Orthodontic Specialists será completamente indolora.",
  firstConsultationHeading: "Su primera cita de consulta gratuita",
  firstConsultationP1:
    "Creemos que su primera visita de ortodoncia es la más importante. Su primera cita comenzará con un recorrido por nuestro consultorio y una presentación de los miembros de nuestro equipo.",
  firstConsultationP2:
    "Nuestro equipo le dará la bienvenida, le presentará a nuestro ortodoncista, el Dr. García-Paul, y le mostrará nuestro consultorio. Nuestro coordinador de tratamiento de ortodoncia altamente capacitado tomará algunas fotografías digitales de sus dientes. Si es necesario, se tomará una radiografía panorámica y cefalométrica. Estas imágenes son la mejor manera de ver sus dientes, mandíbulas y tejido blando facial.",
  firstConsultationP3:
    "Después de revisar estos registros, el Dr. García-Paul se reunirá con usted en una sala de consulta privada para analizar su plan de tratamiento individualizado y único. El Dr. García-Paul le indicará las áreas específicas que le preocupan y le mostrará qué tipo de correcciones puede esperar mostrándole fotos y videos de casos similares.",
  firstConsultationP4:
    "Durante su examen inicial integral, el Dr. García-Paul desarrollará un plan de tratamiento personalizado que se adapte a sus necesidades específicas. Le ayudaremos a comprender cómo el tratamiento de ortodoncia puede tener un impacto positivo para usted o su hijo.",
  firstConsultationP5:
    "Se tomará todo el tiempo que sea necesario para responder a sus preguntas y decidir qué tipo de tratamiento es el mejor para usted. La Dra. García-Paul y su asistente lo guiarán a través del proceso.",
  firstConsultationP6:
    "Después de su consulta, tendrá amplia oportunidad de reunirse con nuestro coordinador financiero para discutir los costos del tratamiento, las opciones de seguro y los planes de pago.",
  firstConsultationP7:
    "Si está considerando un tratamiento, nuestro equipo estará encantado de responder cualquier pregunta que tenga y ayudarlo a determinar qué tratamiento es el adecuado para usted.",
  firstConsultationP8:
    "Llame a nuestro consultorio ahora para programar su primera consulta gratuita.",
  startingTreatmentHeading: "Inicio del tratamiento",
  startingTreatmentP1:
    "Nuestro consultorio ofrece un programa conveniente que comienza el mismo día. Si está listo para comenzar el tratamiento, podemos colocarle los brackets o realizarle una exploración Invisalign el mismo día de su consulta.",
  startingTreatmentP2:
    "Esto le permite evitar tomarse tiempo libre adicional de la escuela o del trabajo para la cita de vinculación.",
  followUpHeading: "Citas de seguimiento",
  followUpP1:
    "Durante el tratamiento, se programarán citas cada 6 a 8 semanas. La duración de las citas variará entre 20 y 40 minutos, según los ajustes que se requieran.",
  followUpP2:
    "Hacemos todo lo posible para ser puntuales para nuestros pacientes y le pedimos que tenga la misma cortesía con nosotros. Si no puede asistir a una cita programada, comuníquese con nuestra oficina lo antes posible.",
  getStartedHeading: "Empezar",
  getStartedP1:
    "Si tiene más preguntas sobre su primera visita de ortodoncia, llámenos al 305-407-9000.",
  getStartedP2:
    "También puede programar una consulta gratuita completando un sencillo formulario en línea.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function YourFirstVisitPage() {
  const getLocalization = useLocalization(localized);
  return (
    <>
      <HeroStyle2
        heroImageUrl="/same-day.jpg"
        mobileBackgroundPosition="18% 50%"
        title={getLocalization("title")}
        backgroundPosition="center"
      />
      <Flex
        direction="column"
        align="center"
        className={`section ${styles.mainContentSectionOuter}`}
      >
        <Flex
          width="100%"
          gap="8"
          direction="column"
          px="4"
          className={styles.mainContentSection}
        >
          <Flex width="100%" gap="5" direction="column">
            <Heading as="h2" className={styles.heading}>
              {getLocalization("startYourNewSmileHeading")}
            </Heading>
            <Text size="5">{getLocalization("startYourNewSmileP1")}</Text>
            <Text size="5">{getLocalization("startYourNewSmileP2")}</Text>
          </Flex>
          <Flex width="100%" gap="5" direction="column">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("firstConsultationHeading")}
            </Heading>
            <Text size="5">{getLocalization("firstConsultationP1")}</Text>
            <Text size="5">{getLocalization("firstConsultationP2")}</Text>
            <Text size="5">{getLocalization("firstConsultationP3")}</Text>
            <Text size="5">{getLocalization("firstConsultationP4")}</Text>
            <Text size="5">{getLocalization("firstConsultationP5")}</Text>
            <Text size="5">{getLocalization("firstConsultationP6")}</Text>
            <Text size="5">{getLocalization("firstConsultationP7")}</Text>
            <Text size="5">{getLocalization("firstConsultationP8")}</Text>
          </Flex>
          <Box>
            <OptimizedImage
              width="2000"
              height="1335"
              className={styles.inlineImage}
              src="/finance.jpg"
              alt="Orthodontist in Miami discussing treatment plan with patients, showcasing braces options and personalized care at Miami Orthodontic Specialists."
            />
          </Box>
          <Flex gap="5" direction="row" wrap="wrap" width="100%">
            <Flex
              flexBasis={{ initial: "100%", sm: "47%" }}
              gap="4"
              direction="column"
              minWidth="40%"
            >
              <Heading as="h3" className={styles.heading3}>
                {getLocalization("startingTreatmentHeading")}
              </Heading>
              <Text size="5">{getLocalization("startingTreatmentP1")}</Text>
              <Text size="5">{getLocalization("startingTreatmentP2")}</Text>
            </Flex>

            <Flex
              flexBasis={{ initial: "100%", sm: "47%" }}
              gap="4"
              direction="column"
              minWidth="40%"
            >
              <Heading as="h3" className={styles.heading3}>
                {getLocalization("followUpHeading")}
              </Heading>
              <Text size="5">{getLocalization("followUpP1")}</Text>
              <Text size="5">{getLocalization("followUpP2")}</Text>
              <Heading as="h3" className={styles.heading3}>
                {getLocalization("getStartedHeading")}
              </Heading>
              <Text size="5">{getLocalization("getStartedP1")}</Text>
              <Text size="5">{getLocalization("getStartedP2")}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
